/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Renders small thumbnail canvases for all pages.
 * Returns a map of pageNumber → HTMLCanvasElement.
 */
export const useThumbnailRenderer = (
	pdfDoc: React.MutableRefObject<any>,
	totalPages: number
) => {
	const canvasRefs = useRef<Map<number, HTMLCanvasElement>>(new Map())
	const renderingRef = useRef<Set<number>>(new Set())
	const [renderedCount, setRenderedCount] = useState(0)

	const renderThumbnail = useCallback(
		async (pageNumber: number) => {
			if (!pdfDoc.current || renderingRef.current.has(pageNumber)) return
			renderingRef.current.add(pageNumber)

			try {
				const page = await pdfDoc.current.getPage(pageNumber)
				const viewport = page.getViewport({ scale: 1 })
				const thumbnailWidth = 120
				const scale = thumbnailWidth / viewport.width
				const thumbViewport = page.getViewport({ scale })
				const dpr = Math.min(2, Math.max(1, window.devicePixelRatio || 1))

				let canvas = canvasRefs.current.get(pageNumber)
				if (!canvas) {
					canvas = document.createElement("canvas")
					canvas.style.display = "block"
					canvas.style.width = `${thumbViewport.width}px`
					canvas.style.height = `${thumbViewport.height}px`
					canvas.style.cursor = "pointer"
					canvasRefs.current.set(pageNumber, canvas)
				}

				const ctx = canvas.getContext("2d")
				if (!ctx) return

				canvas.width = Math.max(1, Math.floor(thumbViewport.width * dpr))
				canvas.height = Math.max(
					1,
					Math.floor(thumbViewport.height * dpr)
				)

				await page.render({
					canvasContext: ctx,
					viewport: thumbViewport,
					transform:
						dpr !== 1 ? ([dpr, 0, 0, dpr, 0, 0] as any) : undefined,
				}).promise

				setRenderedCount(c => c + 1)
			} catch (e) {
				console.error(`Thumbnail render error page ${pageNumber}:`, e)
			} finally {
				renderingRef.current.delete(pageNumber)
			}
		},
		[pdfDoc]
	)

	useEffect(() => {
		if (!pdfDoc.current || totalPages === 0) return
		for (let i = 1; i <= totalPages; i++) {
			renderThumbnail(i)
		}
	}, [totalPages, renderThumbnail])

	return { thumbnailCanvases: canvasRefs, renderedCount }
}
